// src/components/settings/ObservabilityTab.tsx

/**
 * Observability settings tab for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - presenting the current observability configuration from page-level settings
 * - allowing administrators to update Prometheus connection settings
 * - testing connectivity against the configured observability endpoint
 * - surfacing backend validation and connectivity errors in a readable format
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';

import { testObservabilityConnection } from '../../api/observabilityApi';
import { formatApiError } from '../../utils/formatApiError';
import { DOC_PATHS } from '../../utils/onboardingResources';
import ActionButton from '../common/ActionButton';
import ResourceLink from '../common/ResourceLink';
import DynatraceConnection from './DynatraceConnection';
import ActiveMetricsProvider from './ActiveMetricsProvider';
/**
 * Props accepted by the observability settings tab.
 */
interface ObservabilityTabProps {
  onProviderSaved?: (provider: string, enabled: boolean) => void;
  projectId?: string;
  /** Indicates whether the current user can modify settings. */
  isAdmin: boolean;
  sampleOnlyMode?: boolean;

  /** Canonical page-level settings payload. */
  settings: {
    activeMetricsProvider?: string;
    prometheusUrl: string;
    inheritedPrometheusUrl?: string;
    prometheusProfileConfigured?: boolean;
    prometheusConfigSource?: string;
    prometheusProfileReady?: boolean;
    prometheusDiscoveredJobs?: string[];
    prometheusValidJobs?: string[];
    prometheusSelectedJob?: string | null;
    prometheusJobAmbiguous?: boolean;
    prometheusJobValidationError?: string | null;
    queryRange: string;
    observabilityEnabled: boolean;
  };

  /** Persist a partial canonical settings update. */
  onSave: (updates: {
    prometheusUrl: string;
    prometheusSelectedJob?: string | null;
    queryRange: string;
    observabilityEnabled?: boolean;
  }) => Promise<void>;
}

/**
 * Supported lookback ranges exposed by the UI.
 */
const TIME_RANGES = [
  { label: '1 minute', value: '1m' },
  { label: '5 minutes', value: '5m' },
  { label: '15 minutes', value: '15m' },
  { label: '30 minutes', value: '30m' },
  { label: '1 hour', value: '1h' },
];

/**
 * Render the observability settings tab.
 *
 * @param props - Component props.
 * @returns The observability settings tab.
 */
const ObservabilityTab: React.FC<ObservabilityTabProps> = ({
  projectId,
  isAdmin,
  sampleOnlyMode = false,
  settings,
  onSave,
  onProviderSaved,
}) => {
  const urlInputRef = useRef<HTMLInputElement | null>(null);
  const inheritedUrl = settings.inheritedPrometheusUrl || '';
  const displayedUrl = settings.prometheusUrl || inheritedUrl || '';
  const [url, setUrl] = useState(displayedUrl);
  const [range, setRange] = useState(settings.queryRange || '15m');
  const [selectedJob, setSelectedJob] = useState(settings.prometheusSelectedJob || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  useEffect(() => {
    setUrl(settings.prometheusUrl || settings.inheritedPrometheusUrl || '');
    setRange(settings.queryRange || '15m');
    setSelectedJob(settings.prometheusSelectedJob || '');
  }, [
    settings.prometheusUrl,
    settings.inheritedPrometheusUrl,
    settings.queryRange,
    settings.prometheusSelectedJob,
    settings.observabilityEnabled,
  ]);

  const discoveredJobs = settings.prometheusDiscoveredJobs || [];
  const validJobs = settings.prometheusValidJobs || [];
  const selectableJobs = validJobs.length > 0 ? validJobs : discoveredJobs;
  const showJobSelector =
    !sampleOnlyMode &&
    settings.prometheusProfileConfigured &&
    (settings.prometheusJobAmbiguous || selectableJobs.length > 1 || !!settings.prometheusSelectedJob);

  /**
   * Extract a human-readable error message from different backend error shapes.
   *
   * @param err - Raw error object.
   * @returns Readable error message for the UI.
   */
  const extractErrorMessage = (err: any): string => {
    if (err.response) {
      try {
        const errorData =
          typeof err.response === 'string'
            ? JSON.parse(err.response)
            : err.response;

        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            return errorData.detail
              .map((entry: any) => `${entry.loc?.join('.') || 'Field'}: ${entry.msg}`)
              .join(', ');
          }

          return errorData.detail;
        }

        if (errorData.message) {
          return errorData.message;
        }
      } catch (parseError) {
        console.error(
          '[OBSERVABILITY] Failed to parse error response:',
          parseError
        );
      }
    }

    return formatApiError(err, 'Request failed. Check console for details.');
  };

  /**
   * Test the connectivity of the configured Prometheus endpoint.
   *
   * Raw fetch is intentionally preserved here to expose the full backend
   * response body during troubleshooting.
   */
  const testConnection = async () => {
    setTesting(true);
    setError(null);
    setTestSuccess(false);

    try {
      const payload = {
        prometheus_url: url,
        prometheus_lookback: range,
      };

      const rawResponse = await testObservabilityConnection(undefined, payload);

      const responseText = await rawResponse.text();

      if (!rawResponse.ok) {
        let errorData;

        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { detail: responseText };
        }

        console.error('[OBSERVABILITY] Backend error:', errorData);

        let errorMsg = 'Connection test failed';

        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMsg = errorData.detail
              .map((entry: any) => {
                const field = entry.loc ? entry.loc.slice(1).join('.') : 'unknown';
                return `${field}: ${entry.msg}`;
              })
              .join('\n');
          } else {
            errorMsg = errorData.detail;
          }
        } else if (errorData.message) {
          errorMsg = errorData.message;
        }

        setError(errorMsg);
        return;
      }

      const result = JSON.parse(responseText);

      setTestSuccess(true);
      setTimeout(() => setTestSuccess(false), 5000);
    } catch (err: any) {
      console.error('[OBSERVABILITY] Connection test failed:', err);
      setError(extractErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  /**
   * Save the current observability configuration.
   *
   * The parent resolves only after persistence and configuration retrieval.
   */
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await onSave({
        prometheusUrl: url,
        prometheusSelectedJob: selectedJob || null,
        queryRange: range,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('[OBSERVABILITY] Failed to save configuration:', err);
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">Observability Settings</Typography>
      <Divider sx={{ mb: 3 }} />
      {projectId && <ActiveMetricsProvider key={projectId} projectId={projectId} initialProvider={settings.activeMetricsProvider} initialEnabled={settings.observabilityEnabled} onProviderSaved={onProviderSaved} editable={isAdmin && !sampleOnlyMode} />}
      <Box data-testid="observability-providers" sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3, alignItems: 'stretch', minWidth: 0 }}>
      <Paper component="section" aria-label="Prometheus configuration" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">Prometheus connection</Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>Saved connection: {settings.prometheusProfileConfigured ? 'Configured' : 'Unconfigured'}</Typography>
      <ResourceLink
        href={DOC_PATHS.observabilitySetup}
        target="_blank"
        sx={{ mb: 2 }}
      >
        View observability setup guide
      </ResourceLink>

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      {testSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Connection test successful!
        </Alert>
      )}

      {sampleOnlyMode && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Available for real projects only
          </Typography>
          <Typography variant="body2">
            Sample projects use demo tests and sample execution history. Connect observability after you upload your own library or create a real project.
          </Typography>
        </Alert>
      )}

      {!sampleOnlyMode && !settings.prometheusProfileConfigured && inheritedUrl.trim() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          An organization Prometheus URL is available, but this project has no observability profile yet. Save settings to create the project profile.
        </Alert>
      )}

      {!sampleOnlyMode && settings.prometheusProfileConfigured && !settings.prometheusProfileReady && (
        <Alert severity={settings.prometheusJobAmbiguous ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {settings.prometheusJobValidationError ||
            'Project observability is saved, but runtime metrics need one more configuration step.'}
        </Alert>
      )}

      {!sampleOnlyMode && !url.trim() && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: 'rgba(0, 180, 255, 0.08)',
            border: '1px solid rgba(0, 180, 255, 0.18)',
          }}
          action={
            isAdmin ? (
              <ActionButton
                size="small"
                onClick={() => urlInputRef.current?.focus()}
              >
                Connect observability
              </ActionButton>
            ) : undefined
          }
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Observability not connected
          </Typography>
          <Typography variant="body2">
            Connect Prometheus when you are ready to use runtime-aware selection.
          </Typography>
        </Alert>
      )}

      <TextField
        label="Prometheus URL"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        inputRef={urlInputRef}
        fullWidth
        margin="normal"
        disabled={!isAdmin || sampleOnlyMode}
        placeholder="e.g. http://35.198.133.114:9090"
      />

      <FormControl fullWidth margin="normal" disabled={!isAdmin || sampleOnlyMode}>
        <InputLabel id="time-range-label">Time Range</InputLabel>
        <Select
          labelId="time-range-label"
          value={range}
          onChange={(event) => setRange(event.target.value)}
          label="Time Range"
        >
          {TIME_RANGES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showJobSelector && (
        <FormControl fullWidth margin="normal" disabled={!isAdmin || sampleOnlyMode}>
          <InputLabel id="prometheus-job-label">Project Prometheus job</InputLabel>
          <Select
            labelId="prometheus-job-label"
            value={selectedJob}
            onChange={(event) => setSelectedJob(event.target.value)}
            label="Project Prometheus job"
          >
            <MenuItem value="">
              Select the job for this project
            </MenuItem>
            {selectableJobs.map((job) => (
              <MenuItem key={job} value={job}>
                {job}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Monitored metrics
      </Typography>

      <Box sx={{ pl: 1 }}>
        <Typography variant="body2">• CPU Load</Typography>
        <Typography variant="body2">• Error Rate</Typography>
        <Typography variant="body2">• Response Time</Typography>
      </Box>
      {isAdmin && !sampleOnlyMode && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 3, mt: 'auto' }}>
          <ActionButton
            onClick={() => void testConnection()}
            disabled={testing || !url}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </ActionButton>

          <ActionButton
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Connection'}
          </ActionButton>
          <ActionButton disabled={saving || !settings.prometheusProfileConfigured} onClick={async () => {
            setSaving(true); setError(null);
            try { await onSave({ prometheusUrl: '', prometheusSelectedJob: null, queryRange: range }); setUrl(''); }
            catch (err) { setError(extractErrorMessage(err)); }
            finally { setSaving(false); }
          }}>Remove saved connection</ActionButton>
          <Typography variant="caption">Removal applies only to this project. The selected provider is retained.</Typography>
        </Box>
      )}

      </Paper>
      <Paper component="section" aria-label="Dynatrace configuration" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {projectId ? <DynatraceConnection key={projectId} projectId={projectId} editable={isAdmin && !sampleOnlyMode} /> : <Typography>Select a project to configure Dynatrace.</Typography>}
      </Paper>
      </Box>
    </Box>
  );
};

export default ObservabilityTab;
